import styled from '@emotion/styled';
import { theme } from '../../styles/theme';

interface LiveEvent {
  id: string;
  name: string;
  eventDate: string; // UTC time: "2026-01-15 09:00:00"
  duration: string; // minutes
  coverImage: string;
  tags: string;
}

interface LiveEventsProps {
  events: LiveEvent[];
  onEventClick?: (eventId: string) => void;
}

const Container = styled.div`
  width: 100%;
  margin: ${theme.spacing.md} 0;
`;

const ScrollContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  overflow-x: auto;
  padding: 0 ${theme.spacing.md};
  padding-bottom: ${theme.spacing.xs};
  scroll-behavior: smooth;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const Card = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 280px;
  cursor: pointer;
  transition: transform ${theme.transitions.fast};

  &:active {
    transform: scale(0.98);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: ${theme.spacing.sm};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DurationBadge = styled.div`
  position: absolute;
  top: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(10px);
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #FFFFFF;
  font-family: ${theme.typography.fontFamily.primary};
`;

const TagsBadge = styled.div`
  position: absolute;
  bottom: ${theme.spacing.sm};
  left: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(10px);
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  color: #FFFFFF;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-family: ${theme.typography.fontFamily.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const EventName = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EventTime = styled.div`
  font-size: 13px;
  color: var(--color-text-secondary);
  font-family: ${theme.typography.fontFamily.primary};
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '🕐';
    font-size: 14px;
  }
`;

const EventDate = styled.div`
  font-size: 12px;
  color: var(--color-text-muted);
  font-family: ${theme.typography.fontFamily.primary};
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '📅';
    font-size: 12px;
  }
`;

/**
 * Convert UTC time to user's local timezone
 */
function convertUTCToLocal(utcDateString: string): {
  date: string;
  time: string;
  dayOfWeek: string;
} {
  // Parse UTC time: "2026-01-15 09:00:00"
  const [datePart, timePart] = utcDateString.split(' ');
  const [year, month, day] = datePart.split('-');
  const [hour, minute] = timePart.split(':');

  // Create UTC date
  const utcDate = new Date(Date.UTC(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hour),
    parseInt(minute),
    0
  ));

  // Format in user's local timezone
  const localDate = utcDate.toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
  });

  const localTime = utcDate.toLocaleTimeString('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const dayOfWeek = utcDate.toLocaleDateString('tr-TR', {
    weekday: 'long',
  });

  return {
    date: localDate,
    time: localTime,
    dayOfWeek: dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1),
  };
}

/**
 * Format duration from minutes to readable format
 */
function formatDuration(minutes: string): string {
  const mins = parseInt(minutes);
  if (mins < 60) {
    return `${mins} dk`;
  }
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  if (remainingMins === 0) {
    return `${hours} sa`;
  }
  return `${hours}sa ${remainingMins}dk`;
}

export const LiveEvents: React.FC<LiveEventsProps> = ({ events, onEventClick }) => {
  return (
    <Container>
      <ScrollContainer>
        {events.map((event) => {
          const { date, time, dayOfWeek } = convertUTCToLocal(event.eventDate);
          const duration = formatDuration(event.duration);

          return (
            <Card
              key={event.id}
              onClick={() => onEventClick?.(event.id)}
            >
              <ImageContainer>
                <Image
                  src={event.coverImage || 'https://via.placeholder.com/280x180'}
                  alt={event.name}
                />
                <DurationBadge>{duration}</DurationBadge>
                {event.tags && <TagsBadge>{event.tags}</TagsBadge>}
              </ImageContainer>

              <InfoContainer>
                <EventName>{event.name}</EventName>
                <EventTime>{time}</EventTime>
                <EventDate>{dayOfWeek}, {date}</EventDate>
              </InfoContainer>
            </Card>
          );
        })}
      </ScrollContainer>
    </Container>
  );
};
